import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// Configuration
const CHUNK_SIZE = 30 * 1024 * 1024; // 10MB chunks
const LARGE_FILE_THRESHOLD = 100 * 1024 * 1024; // 50MB threshold for chunking
const MAX_RETRIES = 3;
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:2001';

interface UploadOptions {
  file: File;
  visitorId: string;
  onProgress?: (progress: number) => void;
  onError?: (error: Error) => void;
  onSuccess?: (data: any) => void;
  token?: string | null;
}

/**
 * Handles video upload with automatic chunking for large files
 * This provides a unified way to upload videos of any size
 */
export const uploadVideo = async ({
  file,
  visitorId,
  onProgress,
  onError,
  onSuccess,
  token
}: UploadOptions) => {
  // For small videos, use regular upload
  if (file.size <= LARGE_FILE_THRESHOLD) {
    return uploadRegularVideo({ file, visitorId, onProgress, onError, onSuccess, token });
  }
  
  // For large videos, use chunked upload
  return uploadLargeVideo({ file, visitorId, onProgress, onError, onSuccess, token });
};

/**
 * Regular upload for smaller videos
 */
const uploadRegularVideo = async (options: UploadOptions) => {
  const { file, visitorId, onProgress, onError, onSuccess, token } = options;
  
  try {
    // Create form data
    const formData = new FormData();
    formData.append('visitorId', visitorId);
    formData.append('mediaType', 'video');
    formData.append('media', file);
    
    // Create headers
    const headers: any = {
      'Content-Type': 'multipart/form-data'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Upload file
    const response = await axios.post(
      `${API_BASE_URL}/api/media/upload`,
      formData,
      {
        headers,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percentCompleted);
          }
        }
      }
    );
    
    // Handle success
    if (onSuccess) {
      onSuccess(response.data);
    }
    
    return response.data;
  } catch (error: any) {
    console.error('Error uploading video:', error);
    
    if (onError) {
      onError(error);
    }
    
    throw error;
  }
};

/**
 * Chunked upload for large videos
 */
const uploadLargeVideo = async (options: UploadOptions) => {
  const { file, visitorId, onProgress, onError, onSuccess, token } = options;
  
  try {
    // Calculate total chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    
    // Create headers for authentication
    const headers: any = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Step 1: Initiate the chunked upload
    const initiateResponse = await axios.post(
      `${API_BASE_URL}/api/upload/chunked/initiate`,
      {
        filename: file.name,
        totalChunks,
        visitorId,
        mediaType: 'video'
      },
      { headers }
    );
    
    const { uploadId } = initiateResponse.data;
    
    // Step 2: Upload each chunk
    let uploadedChunks = 0;
    
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Create form data for this chunk
      const formData = new FormData();
      formData.append('chunk', chunk, `${file.name}.part${i}`);
      formData.append('chunkIndex', i.toString());
      
      // Try uploading this chunk with retries
      let retries = 0;
      let chunkUploaded = false;
      
      while (!chunkUploaded && retries < MAX_RETRIES) {
        try {
          await axios.post(
            `${API_BASE_URL}/api/upload/chunked/chunk/${uploadId}`,
            formData,
            {
              headers: {
                ...headers,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          chunkUploaded = true;
          uploadedChunks++;
          
          // Update progress
          if (onProgress) {
            onProgress((uploadedChunks / totalChunks) * 100);
          }
        } catch (error) {
          retries++;
          console.error(`Error uploading chunk ${i}. Retry ${retries}/${MAX_RETRIES}`, error);
          
          if (retries >= MAX_RETRIES) {
            throw new Error(`Failed to upload chunk ${i} after ${MAX_RETRIES} retries`);
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
    
    // Step 3: Complete the upload
    const completeResponse = await axios.post(
      `${API_BASE_URL}/api/upload/chunked/complete/${uploadId}`,
      {},
      { headers }
    );
    
    if (onSuccess) {
      onSuccess(completeResponse.data);
    }
    
    return completeResponse.data;
  } catch (error: any) {
    console.error('Error in chunked upload:', error);
    
    if (onError) {
      onError(error);
    }
    
    throw error;
  }
};

/**
 * Upload multiple videos with automatic chunking for large files
 */
export const uploadMultipleVideos = async ({
  files,
  visitorId,
  onTotalProgress,
  onVideoProgress,
  onError,
  onSuccess,
  token
}: {
  files: File[];
  visitorId: string;
  onTotalProgress?: (progress: number) => void;
  onVideoProgress?: (fileIndex: number, progress: number) => void;
  onError?: (error: Error, fileIndex?: number) => void;
  onSuccess?: (results: any[]) => void;
  token?: string | null;
}) => {
  const results: any[] = [];
  let completedFiles = 0;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      const result = await uploadVideo({
        file,
        visitorId,
        token,
        onProgress: (progress) => {
          if (onVideoProgress) {
            onVideoProgress(i, progress);
          }
          
          if (onTotalProgress) {
            const totalProgress = ((completedFiles * 100) + progress) / files.length;
            onTotalProgress(totalProgress);
          }
        },
        onError: (error) => {
          if (onError) {
            onError(error, i);
          }
        }
      });
      
      results.push(result);
      completedFiles++;
      
      if (onTotalProgress) {
        const totalProgress = (completedFiles * 100) / files.length;
        onTotalProgress(totalProgress);
      }
    } catch (error: any) {
      console.error(`Error uploading file ${i}:`, error);
      // Continue with next file
    }
  }
  
  if (onSuccess) {
    onSuccess(results);
  }
  
  return results;
};
